import { ClickAwayListener, Paper, Popper, useTheme } from "@mui/material";
import { OptionsList } from "./OptionsList";
import { SearchHeader } from "./SearchHeader";
import { SelectedOptionsSection } from "./SelectedOptionsSection";
import { MaskMultiSelectController, MData } from "./types";

interface MultiSelectPopoverProps<T extends MData> {
  controller: MaskMultiSelectController<T>;
  optionWithSelectAll?: boolean;
  renderCustomLabel?: (opt: T) => React.ReactNode;
  onOptionsScroll?: (el: HTMLElement | null) => void;
  loadingMore?: boolean;
}

export const MultiSelectPopover = <T extends MData>({
  controller,
  optionWithSelectAll = false,
  renderCustomLabel,
  onOptionsScroll,
  loadingMore,
}: MultiSelectPopoverProps<T>) => {
  const theme = useTheme();

  return (
    <Popper
      open={controller.open}
      anchorEl={controller.anchorRef.current}
      placement="bottom-start"
      style={{
        zIndex: theme.zIndex.modal,
        width: controller.anchorRef.current?.offsetWidth,
      }}
      modifiers={[{ name: "offset", options: { offset: [0, 4] } }]}
    >
      <ClickAwayListener onClickAway={controller.closePopup}>
        <Paper
          elevation={4}
          sx={{
            width: "100%",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <SelectedOptionsSection
            selectedItems={controller.selectedItems}
            labelKey={controller.labelKey}
            showAllSelected={controller.showAllSelected}
            onToggleShowAll={controller.toggleShowAllSelected}
            onToggleOption={controller.toggleOption}
            onClearAll={controller.clearAll}
            renderCustomLabel={renderCustomLabel}
          />

          <SearchHeader
            value={controller.searchTerm}
            resultCount={controller.unselectedFiltered.length}
            onChange={controller.handleSearchChange}
          />

          <OptionsList
            options={controller.unselectedFiltered}
            labelKey={controller.labelKey}
            idKey={controller.idKey}
            showSelectAll={optionWithSelectAll}
            allSelected={controller.allSelected}
            onToggleSelectAll={controller.toggleSelectAll}
            onToggleOption={controller.toggleOption}
            renderCustomLabel={renderCustomLabel}
            onScroll={onOptionsScroll}
            loading={loadingMore}
          />
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};
